import {DOCUMENT} from '@angular/common';
import {ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {NavigationFunctionData} from '@application/helper/navigation-helper/navigation-function-data.interface';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNewItemData} from '@application/helper/navigation-helper/navigation-new-item-data.interface';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {SourceElementUtils} from '@application/helper/source-element-utils';
import {StringUtils} from '@application/helper/string-utils';
import {getMappedColoredYarnSet} from '@application/helper/textile-data/colored-yarn-set/get-mapped-colored-yarn-set';
import {NavigationHistoryService} from '@application/navigation-history/navigation-history.service';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {CustomSettings} from '@domain/custom-settings/custom-settings';
import {Equipment} from '@domain/machine/equipment';
import {MachineType} from '@domain/machine/machine-type.enum';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {ProductConfiguration} from '@domain/product-configuration/product-configuration';
import {SelectListProductConfiguration} from '@domain/product-configuration/select-list-product-configuration';
import {SelectListProductConfigurationVersionHistory} from '@domain/product-configuration/select-list-product-configuration-version-history';
import {VersionHistory} from '@domain/product-configuration/version-history';
import {BmpProductionScheduleItemInPathGroup} from '@domain/production-schedule/bmp-production-schedule-item-in-path-group';
import {BuggyForProductionSchedule} from '@domain/production-schedule/buggy-for-production-schedule';
import {CreelForProductionSchedule} from '@domain/production-schedule/creel-for-production-schedule';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {ProductionOrderNameGenerationView} from '@domain/production-schedule/custom-settings/production-order-name-generation-view.interface';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {FinishingForProductionSchedule} from '@domain/production-schedule/finishing-for-production-schedule';
import {InitialOptionsForProductionOrder} from '@domain/production-schedule/initial-options-for-production-order';
import {InitialPathLabel} from '@domain/production-schedule/initial-path-label';
import {MachineQualityForProductionSchedule} from '@domain/production-schedule/machine-quality-for-production-schedule';
import {MachineSummary} from '@domain/production-schedule/machine-summary';
import {PathLabelInformation} from '@domain/production-schedule/path-label-information';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionScheduleProductConfiguration} from '@domain/production-schedule/production-schedule-product-configuration';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {InitialRestZone} from '@domain/production-schedule/rest-zone/initial-rest-zone';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription as ProfileSubscription} from '@domain/profile/subscription';
import {PropertyValue} from '@domain/property-value';
import {CreelMap} from '@domain/textile-data/creel/creel-map';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {OverviewListCreelPosition} from '@domain/textile-data/creel/overview-list-creel-position';
import {CarpetWeaveQuality} from '@domain/textile-data/machine-quality/carpet-weave-quality';
import {PathWidth} from '@domain/textile-data/machine-quality/path-width';
import {WeaveQuality} from '@domain/textile-data/machine-quality/weave-quality';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {HttpBuggyCustomSettingsService} from '@infrastructure/http/buggy/http-buggy-custom-settings.service';
import {HttpBuggyService} from '@infrastructure/http/buggy/http-buggy.service';
import {CREELS, Creels} from '@infrastructure/http/creel/creels';
import {MACHINE_QUALITIES, MachineQualities} from '@infrastructure/http/machine-quality/machine-qualities';
import {MACHINES, Machines} from '@infrastructure/http/machine/machines.interface';
import {HttpPlanningPrototypeService} from '@infrastructure/http/planning-prototype/http-planning-prototype.service';
import {PLANNING, Planning} from '@infrastructure/http/planning/planning';
import {PRODUCT_CONFIGURATIONS, ProductConfigurations} from '@infrastructure/http/product-configuration/product-configuration.interface';
import {HttpProductionOrdersCustomSettingsService} from '@infrastructure/http/production-order-custom-settings/http-production-orders-custom-settings.service';
import {PRODUCTION_ORDERS, ProductionOrders} from '@infrastructure/http/production-order/production-orders';
import {PRODUCTION_SCHEDULES, ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {SelectBuggyComponent} from '@presentation/components/select-buggy/select-buggy.component';
import {SelectCreelComponent} from '@presentation/components/select-creel/select-creel.component';
import {SelectFinishingComponent} from '@presentation/components/select-finishing/select-finishing.component';
import {SelectMachineQualityComponent} from '@presentation/components/select-machine-quality/select-machine-quality.component';
import {MachineOverviewList} from '@presentation/pages/machine-overview/machine-overview-list';
import {AddProductionSchedule} from '@presentation/pages/texfab/production-schedule/add/add-production-schedule';
import {ADD_PRODUCTION_SCHEDULE} from '@presentation/pages/texfab/production-schedule/add/add-production-schedule.service';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {BuggySelectionService} from '@presentation/pages/textile-data/buggy/buggy-selection.service';
import {OverviewListColoredYarnSet} from '@presentation/pages/textile-data/colored-yarn-set/overview/overview-list-colored-yarn-set';
import {OverviewListCreel} from '@presentation/pages/textile-data/creel/overview/overview-list-creel';
import {OverviewListMachineQuality} from '@presentation/pages/textile-data/machine-quality/overview/overview-list-machine-quality';
import {WeaveQualityType} from '@presentation/pages/textile-data/machine-quality/overview/weave-quality-type.enum';
import {TextileDataDetailComponent} from '@presentation/pages/textile-data/textile-data-detail/textile-data-detail.component';
import {
  AgGridRowSelection,
  AssertionUtils,
  DialogBuilderFactoryService,
  DialogComponentData,
  FormValidationHelper,
  ObjectUtils,
  TranslateService,
  Unit,
  convertUnit,
  skeletonViewAnimation
} from '@vdw/angular-component-library';
import {cloneDeep} from 'lodash-es';
import {EMPTY, Observable, Subject, Subscription, forkJoin, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {ProductionScheduleData} from '../add-production-schedule-navigation-data.type';
import {BaseAddProductionSchedulePageComponent, ProductionScheduleInitialPreparationsFormGroup} from '../base-add-production-schedule-page.component';
import {ProductionScheduleInitialPreparationsAdditionalOptionsComponent} from './additional-options/production-schedule-initial-preparations-additional-options.component';
import {ProductionScheduleJuteOptionsComponent} from './additional-options/production-schedule-jute-options/production-schedule-jute-options.component';
import {ProductionSchedulePathLabelsComponent} from './additional-options/production-schedule-path-labels/production-schedule-path-labels.component';
import {ProductionScheduleQualityLinkedSettingsComponent} from './additional-options/production-schedule-quality-linked-settings/production-schedule-quality-linked-settings.component';
import {InitialProductionOrderForTypeRequestProperties} from './initial-production-order-for-type-request-properties.type';
import {SelectMachineComponent} from './select-machine/select-machine.component';
import {SelectProductConfigurationComponent} from './select-product-configuration/select-product-configuration.component';

@Component({
  selector: 'app-production-schedule-initial-preparations',
  templateUrl: './production-schedule-initial-preparations.component.html',
  styleUrls: ['./production-schedule-initial-preparations.component.scss'],
  animations: [skeletonViewAnimation('.form-field-skeleton-wrapper, header .url, .warning-for-no-possible-matches-with-current-machine, .mat-expansion-panel')]
})
export class ProductionScheduleInitialPreparationsComponent extends BaseAddProductionSchedulePageComponent implements OnInit {
  @Input() public customSettings: ProductionOrderCustomSettings;
  @Input() public listOfMachineQualityCustomSettings: PropertyValue[] = [];
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public needsToShowValidationFeedback = false;
  @Input() public productionScheduleInitialPreparationsForm: ProductionScheduleInitialPreparationsFormGroup;
  @Input() public productionScheduleStatus: ProductionScheduleStatus;
  @Input() public areAllFieldsDisabled: boolean = false;

  @Output() public restZoneLoaded = new EventEmitter<void>();
  @Output() public freeZoneLoaded = new EventEmitter<void>();
  @Output() public pathZoneLoaded = new EventEmitter<void>();
  @Output() public juteZoneLoaded = new EventEmitter<void>();
  @Output() public formValuesChanged = new EventEmitter<void>();
  @Output() public hideParentHeader = new EventEmitter<boolean>();

  @ViewChild('initialPreparationsAdditionalOptionsComponent') public initialPreparationsAdditionalOptionsComponent: ProductionScheduleInitialPreparationsAdditionalOptionsComponent;
  @ViewChild('pathLabelsComponent') public pathLabelsComponent: ProductionSchedulePathLabelsComponent;
  @ViewChild('juteOptionsComponent') public juteOptionsComponent: ProductionScheduleJuteOptionsComponent;
  @ViewChild('qualityLinkedSettingsComponent') public qualityLinkedSettingsComponent: ProductionScheduleQualityLinkedSettingsComponent;
  public readonly MACHINE_BASE_ROUTER_LINK = RouteUtils.paths.texState.editMachine.absolutePath;
  public readonly PRODUCT_CONFIGURATION_CHIP_VALUE_KEY = '%(nameWithVersion)';
  public readonly PRODUCT_CONFIGURATION_BASE_ROUTER_LINK = RouteUtils.paths.texEdit.editProductConfiguration.absolutePath;
  public readonly QUALITY_CHIP_VALUE_KEY = '%(technicalNameWithVersion)';
  public readonly QUALITY_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.machineQuality.editMachineQuality.absolutePath;
  public readonly BUGGY_BASE_ROUTER_LINK = RouteUtils.paths.utilities.buggy.editBuggy.absolutePath;
  public readonly ADD_JUTE_PERMISSION = Permission.ADD_JUTE;
  public readonly PICKS_PER_METER_UNIT = Unit.PICKS_PER_METER;

  public factorForConversionFromMillimetersToDents: number;
  public factorForConversionFromDentsToMillimeters: number;
  public factorForConversionFromPicksToMillimeters: number;
  public factorForConversionFromMillimetersToPicks: number;
  public pathWidths: PathWidth[];
  public selectedMachine: MachineSummary = null;
  public canShowAdditionalOptions = false;
  public hasPlanningBoardPermission = false;
  public isLoadingMachineQualityAndCreel = false;
  public isLoadingProductionSchedule = false;
  public canShowWarningForIncorrectSplittedCreel: boolean;
  public isViewingCreelMap = false;
  public defaultInitialRestZones: InitialRestZone;
  public oldMachineQuality: MachineQualityForProductionSchedule;
  public canHaveRestAndFreeZonesAndPathLabels: boolean;
  public buggy: BuggyForProductionSchedule;
  public productionScheduleNameGenerationView: ProductionOrderNameGenerationView;
  public refreshNameGeneration: Subject<void> = new Subject();
  public defaultPathLabelInformation: InitialPathLabel;

  private hasBuggyNameGeneration: boolean = false;
  private readonly addPathLabelPermission: Permission = Permission.ADD_PATH_LABEL;
  private readonly dialogBuilderForBasicDialog = this.dialogBuilderFactoryService.getBuilder().withClass('basic-dialog-panel');
  private possibleQualitiesForCurrentMachine: WeaveQuality[];
  private possibleCreelsForCurrentMachine: OverviewListCreel[];
  private productionScheduleInitialPreparationsFormSubject: Subscription;
  private currentSubscription: ProfileSubscription;
  private dialogState: DialogComponentData;
  private hasMachineExceptionForPracticalPickDensity: boolean = false;

  public constructor(
    @Inject(MACHINE_QUALITIES) private readonly machineQualities: MachineQualities,
    @Inject(MACHINES) private readonly machines: Machines,
    @Inject(PRODUCTION_SCHEDULES) private readonly productionSchedules: ProductionSchedules,
    @Inject(PRODUCTION_ORDERS) private readonly productionOrders: ProductionOrders,
    @Inject(CREELS) private readonly creels: Creels,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    @Inject(ADD_PRODUCTION_SCHEDULE) private readonly addProductionSchedule: AddProductionSchedule,
    protected readonly httpProductionOrdersCustomSettings: HttpProductionOrdersCustomSettingsService,
    private readonly httpBuggyCustomSettings: HttpBuggyCustomSettingsService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly navigationHistory: NavigationHistoryService,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(PLANNING) private readonly planning: Planning,
    private readonly planningPrototype: HttpPlanningPrototypeService,
    @Inject(PRODUCT_CONFIGURATIONS) private readonly productConfigurations: ProductConfigurations,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly navigationHelperService: NavigationHelperService<ProductionScheduleData & DialogComponentData>,
    private readonly buggies: HttpBuggyService,
    private readonly buggySelectionService: BuggySelectionService,
    activatedRoute: ActivatedRoute
  ) {
    super(activatedRoute);
  }

  public get selectedMachineName(): string {
    return StringUtils.getPropertyValueOrGeneralPlaceholderIfEmpty(this.selectedMachine, 'name', this.translate);
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.hasPlanningBoardPermission = this.currentSubscription?.hasAtLeastOneOfThePermissions([Permission.TEXFAB_PLANNINGBOARD, Permission.TEXFAB_PLANNINGBOARD_NEW]) ?? false;
    this.initialiseProductionSchedule();

    if (!this.hasAddPathLabelPermission()) {
      this.pathZoneLoaded.emit();
    }

    this.productionSchedule.addToPlanboard ??= this.customSettings.addProductionOrderToPlanningBoard;
    this.updateCanHaveRestAndFreeZonesAndPathLabels();

    this.checkIfBuggyHasNameGeneration();
  }

  public selectMachine(): void {
    this.dialogBuilderForBasicDialog
      .openSelectGridDialog(SelectMachineComponent, {
        machineQualityId: this.isAddingProductionOrderForProductConfiguration() ? this.productionSchedule.machineQuality.id : null,
        allowedMachineTypes: [MachineType.CARPET_LOOM, MachineType.CARPET_LOOM_NO_JQ, MachineType.VELVET]
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedMachine: Equipment) => {
        if (!AssertionUtils.isNullOrUndefined(selectedMachine)) {
          this.onMachineSelect(selectedMachine);
        }
      });
  }

  public toggleOnPlanningBoardSettingValue(value: boolean): void {
    this.productionSchedule.addToPlanboard = value;
  }

  public showColoredYarnSetDetails(event: MouseEvent, productionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent): void {
    const startDent = `${this.translate.instant('GENERAL.START_DENT')}: ${productionScheduleColoredYarnSet.startDent}`;
    const widthInDents = `${this.translate.instant('GENERAL.WIDTH_IN_DENTS')}: ${productionScheduleColoredYarnSet.technicalWidthInDents}`;
    const textileDataAmount = `${startDent}, ${widthInDents}`;

    this.dialogBuilderFactoryService
      .getBuilder()
      .withClass(['reposition-dialog', productionScheduleColoredYarnSet.coloredYarnSet.overviewListColorSet.creelPositions.length > 8 ? 'colored-list-dialog' : ''])
      .withWidth('706px')
      .openDialog(RepositionDialogComponent, {
        textileData: productionScheduleColoredYarnSet.coloredYarnSet,
        textileDataAmount,
        component: TextileDataDetailComponent,
        sourceElement: SourceElementUtils.findSourceElementForDialog(event, 'color-list-preview-container'),
        positionOfDialog: PositionOfDialog.RIGHT,
        creelMap: this.getCreelMap().find((creelMap: CreelMap) => creelMap.coloredYarnSetId === productionScheduleColoredYarnSet.coloredYarnSet.id)
      });
  }

  public canShowWarningForNoPossibleMatchesWithCurrentMachine(): boolean {
    return (
      this.hasChosenMachine() &&
      ((!AssertionUtils.isNullOrUndefined(this.possibleCreelsForCurrentMachine) && this.possibleCreelsForCurrentMachine.length === 0) ||
        (!AssertionUtils.isNullOrUndefined(this.possibleQualitiesForCurrentMachine) && this.possibleQualitiesForCurrentMachine.length === 0)) &&
      !this.isShowingSkeletonView()
    );
  }

  public canShowDetailsOfActiveCreel(): boolean {
    return this.productionSchedule.coloredYarnSets.length > 0 || this.isShowingSkeletonView();
  }

  public selectBuggy(): void {
    const field = this.productionScheduleInitialPreparationsForm.controls.buggy;

    this.buggySelectionService
      .openContentSwitcherDialog(field.value)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((buggy: BuggyForProductionSchedule[]) => {
        if (!AssertionUtils.isNullOrUndefined(buggy)) {
          this.buggy = AssertionUtils.isEmpty(buggy) ? null : buggy[0];
          field.setValue(this.buggy);
        }
      });
  }

  public getSelectedBuggy(): BuggyForProductionSchedule {
    return this.productionScheduleInitialPreparationsForm.get('buggy')?.value;
  }

  public canSelectBuggy(): boolean {
    return this.currentSubscription?.hasPermission(Permission.VIEW_BUGGIES);
  }

  public selectAnotherCreel(): void {
    if (!this.isProductionScheduleRelatedToRunOrBeingDecreated() && !this.areAllFieldsDisabled) {
      if (AssertionUtils.isNullOrUndefined(this.possibleCreelsForCurrentMachine)) {
        this.creels
          .getPossibleCreelsForMachine(this.selectedMachine.id)
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe((possibleCreelsForCurrentMachine: OverviewListCreel[]) => {
            this.possibleCreelsForCurrentMachine = possibleCreelsForCurrentMachine;

            this.selectCreelFromPossibleCreels();
          });
      } else {
        this.selectCreelFromPossibleCreels();
      }
    }
  }

  public selectAnotherMachineQuality(): void {
    if (AssertionUtils.isNullOrUndefined(this.possibleQualitiesForCurrentMachine)) {
      this.machineQualities
        .getPossibleMachineQualitiesForMachine(this.selectedMachine.id)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((possibleQualitiesForCurrentMachine: WeaveQuality[]) => {
          this.possibleQualitiesForCurrentMachine = possibleQualitiesForCurrentMachine;

          this.selectMachineQualityFromPossibleMachineQualities();
        });
    } else {
      this.selectMachineQualityFromPossibleMachineQualities();
    }
  }

  public hasChosenMachine(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.selectedMachine);
  }

  public getProductionSchedule(): ProductionSchedule {
    this.productionSchedule.initialFreeZones = this.initialPreparationsAdditionalOptionsComponent.getInitialFreeZone();
    this.productionSchedule.initialRestZones = this.initialPreparationsAdditionalOptionsComponent.getInitialRestZone();

    const oldFinishingForProductionSchedule: FinishingForProductionSchedule = this.productionSchedule.finishing;

    this.productionSchedule.name = this.productionScheduleInitialPreparationsForm.value.name;
    const practicalPickDensityUnit: Unit = Unit[this.getDefaultParameterForPropertyNameOfMachineQualitySettings('pickDensityUnit').propertyValue as string];
    this.productionSchedule.practicalPickDensityInPicksPerMM = convertUnit({
      from: {
        value: this.productionScheduleInitialPreparationsForm.value.practicalPickDensity,
        unit: practicalPickDensityUnit
      },
      to: Unit.PICKS_PER_MILLIMETER
    });
    this.productionSchedule.productConfiguration = this.productionScheduleInitialPreparationsForm.value.productConfiguration;
    this.productionSchedule.finishing = this.initialPreparationsAdditionalOptionsComponent.getSelectedFinishing();
    this.productionSchedule.dueDate = this.initialPreparationsAdditionalOptionsComponent.getDueDate();
    this.productionSchedule.comment = this.initialPreparationsAdditionalOptionsComponent.getComment();
    this.productionSchedule.buggy = this.getSelectedBuggy();
    this.productionSchedule.pathLabelInformation = this.pathLabelsComponent?.getPathLabelInformation() ?? null;
    this.productionSchedule.juteInformation = this.juteOptionsComponent?.getJuteInformation() ?? null;
    this.productionSchedule.juteGeneralSettings = this.qualityLinkedSettingsComponent?.getGeneralJuteSettings() ?? null;

    this.adjustDefaultFinishingsForProductionScheduleItems(oldFinishingForProductionSchedule);
    return this.productionSchedule;
  }

  public getConfiguredProductionSchedule(): ProductionSchedule {
    const configuredProductionSchedule = cloneDeep(this.productionSchedule);

    configuredProductionSchedule.name = this.productionScheduleInitialPreparationsForm.controls.name.valid ? this.productionScheduleInitialPreparationsForm.value.name : null;

    const practicalPickDensityUnit: Unit = Unit[this.getDefaultParameterForPropertyNameOfMachineQualitySettings('pickDensityUnit').propertyValue as string];
    configuredProductionSchedule.practicalPickDensityInPicksPerMM = !isNaN(this.productionScheduleInitialPreparationsForm.value.practicalPickDensity)
      ? convertUnit({
          from: {
            value: this.productionScheduleInitialPreparationsForm.value.practicalPickDensity,
            unit: practicalPickDensityUnit
          },
          to: Unit.PICKS_PER_MILLIMETER
        })
      : null;

    const oldFinishingForProductionSchedule: FinishingForProductionSchedule = configuredProductionSchedule.finishing;
    const isAdditionalOptionsComponentDefined = !AssertionUtils.isNullOrUndefined(this.initialPreparationsAdditionalOptionsComponent);

    if ((!AssertionUtils.isNullOrUndefined(configuredProductionSchedule.id) || this.isAddingAnyTypeOfProductionOrder()) && isAdditionalOptionsComponentDefined) {
      configuredProductionSchedule.initialFreeZones = this.initialPreparationsAdditionalOptionsComponent.getInitialFreeZone();
      configuredProductionSchedule.initialRestZones = this.initialPreparationsAdditionalOptionsComponent.getInitialRestZone();
      configuredProductionSchedule.finishing = this.initialPreparationsAdditionalOptionsComponent.getSelectedFinishing();
      configuredProductionSchedule.dueDate = this.initialPreparationsAdditionalOptionsComponent.getDueDate();
      configuredProductionSchedule.comment = this.initialPreparationsAdditionalOptionsComponent.getComment();
      configuredProductionSchedule.buggy = this.getSelectedBuggy();
      configuredProductionSchedule.pathLabelInformation = null;
      configuredProductionSchedule.juteInformation = null;
      configuredProductionSchedule.juteGeneralSettings = null;

      if (!AssertionUtils.isNullOrUndefined(this.pathLabelsComponent)) {
        const pathLabelInformation = this.pathLabelsComponent.getPathLabelInformation();

        configuredProductionSchedule.pathLabelInformation = pathLabelInformation;
      }

      if (!AssertionUtils.isNullOrUndefined(this.juteOptionsComponent)) {
        configuredProductionSchedule.juteInformation = this.juteOptionsComponent.getJuteInformation();
      }

      if (!AssertionUtils.isNullOrUndefined(this.qualityLinkedSettingsComponent)) {
        configuredProductionSchedule.juteGeneralSettings = this.qualityLinkedSettingsComponent.getGeneralJuteSettings();
      }
    }

    this.adjustDefaultFinishingsForProductionScheduleItems(oldFinishingForProductionSchedule);
    return configuredProductionSchedule;
  }

  public additionalOptionsChanged(): void {
    this.formValuesChanged.emit();
  }

  public navigateToCustomSettings(): void {
    this.router.navigateByUrl(NavigationUtils.getAbsolutePath(NavigationId.CARPET_PRODUCTION_SCHEDULE_SETTINGS));
  }

  public canShowLabelForMachineQualityAndCreel(): boolean {
    return !this.hasChosenMachine() && !this.isLoadingMachineQualityAndCreel;
  }

  public isShowingSkeletonView(): boolean {
    return this.isLoadingMachineQualityAndCreel || this.isLoadingProductionSchedule;
  }

  public canShowColoredYarnSet(coloredYarnSetIndex: number): boolean {
    return (this.isShowingSkeletonView() && coloredYarnSetIndex < 1) || !this.isShowingSkeletonView();
  }

  public hasChosenMachineQuality(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.productionSchedule.machineQuality);
  }

  public hasListOfMachineQualityCustomSettings(): boolean {
    return this.listOfMachineQualityCustomSettings.length > 0;
  }

  public validateForms(): boolean {
    const forms: FormGroup[] = [this.productionScheduleInitialPreparationsForm];

    if (!AssertionUtils.isNullOrUndefined(this.initialPreparationsAdditionalOptionsComponent)) {
      forms.push(this.initialPreparationsAdditionalOptionsComponent.additionalOptionsForm);
    }

    if (!AssertionUtils.isNullOrUndefined(this.productionSchedule.pathLabelInformation) && this.pathLabelsComponent.pathLabelsForm.enabled) {
      forms.push(this.pathLabelsComponent.pathLabelsForm);
    }

    if (this.canValidateJuteForm()) {
      forms.push(this.juteOptionsComponent.juteForm);
    }

    if (!AssertionUtils.isNullOrUndefined(this.qualityLinkedSettingsComponent)) {
      forms.push(this.qualityLinkedSettingsComponent.qualityLinkedSettingsForm);
    }

    return new FormValidationHelper().checkForms(forms, this.document, this.changeDetectorRef);
  }

  public canShowDisabledFormInputs(): boolean {
    return this.isProductionScheduleRelatedToRunOrBeingDecreated() || this.isAddingProductionOrderForProductConfiguration();
  }

  public canSelectProductConfiguration(): boolean {
    return this.currentSubscription?.hasPermission(Permission.TEXEDIT_CADCAM_PRODUCT_CONFIGURATION_VIEW) ?? false;
  }

  public canInsertPathLabels(): boolean {
    return (
      !AssertionUtils.isNullOrUndefined(this.productionSchedule.pathLabelInformation) &&
      (this.productionSchedule.pathLabelInformation.hasAddedPathLabelBefore() || this.productionSchedule.pathLabelInformation.hasAddedPathLabelAfter())
    );
  }

  public hasAddPathLabelPermission(): boolean {
    return this.currentSubscription?.hasPermission(this.addPathLabelPermission);
  }

  public isProductionScheduleRelatedToRunOrBeingDecreated(): boolean {
    return this.productionSchedule.isRelatedToRun() || this.productionSchedule.isBeingDecreated();
  }

  public applySuggestionForProductionScheduleName(suggestion: string): void {
    this.productionScheduleInitialPreparationsForm.controls.name.setValue(suggestion);
  }

  public canShowJuteOptions(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.productionSchedule.machineQuality) && this.productionSchedule.canAddJute;
  }

  public openCreelMap(): void {
    this.isViewingCreelMap = true;
    this.hideParentHeader.emit(true);
  }

  public closeCreelMap(): void {
    this.isViewingCreelMap = false;
    this.hideParentHeader.emit(false);
  }

  public getCreelMap(): CreelMap[] {
    return this.productionSchedule?.mappingForCurrentPositionOnMachine?.length === 0 || AssertionUtils.isNullOrUndefined(this.productionSchedule?.mappingForCurrentPositionOnMachine)
      ? this.createActiveCreelMap()
      : this.productionSchedule.mappingForCurrentPositionOnMachine;
  }

  public saveCreelMap(activeCreelMap: CreelMap[]): void {
    this.productionSchedule.mappingForCurrentPositionOnMachine = activeCreelMap;
    this.closeCreelMap();
  }

  public getColorList(coloredYarnSet: OverviewListColoredYarnSet): OverviewListColoredYarnSet {
    return getMappedColoredYarnSet(
      coloredYarnSet,
      this.getCreelMap()?.find((creelMap: CreelMap) => creelMap.coloredYarnSetId === coloredYarnSet.id),
      'asc'
    );
  }

  public getCreelMapping(coloredYarnSet: OverviewListColoredYarnSet): CreelMap {
    return this.getCreelMap()?.find((creelMap: CreelMap) => creelMap.coloredYarnSetId === coloredYarnSet.id);
  }

  public selectProductConfiguration(): void {
    this.dialogBuilderForBasicDialog
      .openSelectGridDialog(SelectProductConfigurationComponent, {machineId: this.selectedMachine.id})
      .pipe(
        switchMap((result: {productConfiguration: SelectListProductConfiguration; version: number}) => {
          return result
            ? forkJoin([
                this.productConfigurations.getById(result.productConfiguration.id),
                of(result.productConfiguration.versionHistories.find((versionHistory: VersionHistory) => versionHistory.version === result.version))
              ])
            : EMPTY;
        }),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe(([productConfiguration, selectedVersionHistory]: [ProductConfiguration, SelectListProductConfigurationVersionHistory]) => {
        if (productConfiguration.designs) {
          this.productionSchedule.designs = productConfiguration.designs;
        }

        this.productionScheduleInitialPreparationsForm.controls.productConfiguration.setValue(
          new ProductionScheduleProductConfiguration(productConfiguration.id, productConfiguration.name, selectedVersionHistory.version)
        );

        this.getAndAssignMachineQualityToProductionSchedule(selectedVersionHistory.weaveQuality.id);
      });
  }

  public removeProductConfiguration(): void {
    this.productionScheduleInitialPreparationsForm.controls.productConfiguration.setValue(null);
    this.productionSchedule.designs = [];
  }

  public removeFinishing(): void {
    this.initialPreparationsAdditionalOptionsComponent?.removeFinishing();
    this.productionSchedule.finishing = null;
  }

  public removeBuggy(): void {
    this.productionScheduleInitialPreparationsForm.controls.buggy.setValue(null);
    this.productionSchedule.buggy = undefined;
  }

  public canShowProductConfigurationDisabledFormInput(): boolean {
    return !this.productionSchedule.isRelatedToRun() && !AssertionUtils.isNullOrUndefined(this.productionScheduleInitialPreparationsForm.value.productConfiguration);
  }

  public onPathZoneLoaded(): void {
    this.pathZoneLoaded.emit();
    this.juteZoneLoaded.emit();
  }

  public getWeaveQualityType(): WeaveQualityType {
    return this.selectedMachine.type === MachineType.VELVET ? WeaveQualityType.VELVET : WeaveQualityType.CARPET;
  }

  private canValidateJuteForm(): boolean {
    const juteForm = this.juteOptionsComponent?.juteForm;
    if (AssertionUtils.isNullOrUndefined(juteForm)) {
      return false;
    }

    const beforePathLabelChecked = this.juteOptionsComponent.juteForm.get('hasJuteBefore').value;
    const afterPathLabelChecked = this.juteOptionsComponent.juteForm.get('hasJuteAfter').value;

    if (beforePathLabelChecked && afterPathLabelChecked) {
      return true;
    }

    if (beforePathLabelChecked || afterPathLabelChecked) {
      juteForm.markAllAsTouched();
      juteForm.get(beforePathLabelChecked ? 'juteBefore' : 'juteAfter').updateValueAndValidity();
      juteForm.get('pattern').updateValueAndValidity();
      return false;
    }

    return false;
  }

  private createActiveCreelMap(): CreelMap[] {
    return this.productionSchedule.coloredYarnSets.map((coloredYarnSetsWithStartDent: OverviewListColoredYarnSetWithStartDent) => {
      return {
        coloredYarnSetId: coloredYarnSetsWithStartDent.coloredYarnSet.id,
        creelPositions: coloredYarnSetsWithStartDent.coloredYarnSet.overviewListColorSet.creelPositions.map((creelPosition: OverviewListCreelPosition) => {
          return {
            creelPosition: creelPosition.position,
            currentPositionOnMachine: creelPosition.position
          };
        })
      } as CreelMap;
    });
  }

  private selectCreelFromPossibleCreels(): void {
    this.dialogBuilderForBasicDialog
      .openSelectGridDialog(SelectCreelComponent, {
        creels: this.possibleCreelsForCurrentMachine,
        machineId: this.selectedMachine.id,
        title: this.translate.instant('GENERAL.ACTIONS.SELECT_OBJECT', {object: this.translate.instant('TEXTILE_DATA.CREEL.CREEL', {count: 1}).toLowerCase()})
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedCreels: OverviewListCreel[]) => {
        if (selectedCreels?.length > 0) {
          this.setCreel(selectedCreels[0]);
        }
      });
  }

  private setCreel(creel: OverviewListCreel): void {
    this.assignCreelToProductionSchedule(creel);
    this.formValuesChanged.emit();
    this.notifyChangesToInitialPreparationsForm(true);
  }

  private selectMachineQualityFromPossibleMachineQualities(): void {
    this.dialogBuilderForBasicDialog
      .openSelectGridDialog(SelectMachineQualityComponent, {
        title: this.translate.instant('GENERAL.ACTIONS.SELECT_OBJECT', {
          object: this.translate.instant('GENERAL.POSSIBLE_OBJECTS', {objects: this.translate.instant('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY', {count: 2})}).toLowerCase()
        }),
        rowSelection: AgGridRowSelection.SINGLE,
        allowEmptySelection: false,
        machineId: this.selectedMachine.id
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedWeaveQualities: OverviewListMachineQuality[]) => {
        if (selectedWeaveQualities?.length > 0) {
          const selectedWeaveQuality = selectedWeaveQualities[0];
          this.setQuality(selectedWeaveQuality);
        }
      });
  }

  private setQuality(quality: OverviewListMachineQuality): void {
    if (this.productionSchedule.machineQuality?.id !== quality.id) {
      this.oldMachineQuality = this.productionSchedule.machineQuality;
      this.productionSchedule.productionSchedulePathsOfColoredYarnSets = [];
      this.removeProductConfiguration();
      this.removeFinishing();

      this.getAndAssignMachineQualityToProductionSchedule(quality.id, quality.canAddJute);
    } else {
      this.productionScheduleInitialPreparationsForm.controls.machineQuality.setValue(this.productionSchedule.machineQuality);
    }

    this.productionScheduleInitialPreparationsForm.get('buggy').enable();
  }

  private initialiseProductionSchedule(): void {
    this.isCreatingNewProductionSchedule() ? this.initialiseNewProductionSchedule() : this.initialiseExistingProductionSchedule();
  }

  private isCreatingNewProductionSchedule(): boolean {
    return ObjectUtils.isDeepEqual(this.productionSchedule, ProductionSchedule.createEmptyProductionSchedule()) && !this.isEditingProductionOrder() && !this.isDuplicatingProductionOrder();
  }

  private adjustDefaultFinishingsForProductionScheduleItems(oldFinishingForProductionSchedule: FinishingForProductionSchedule): void {
    this.productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.forEach((productionSchedulePath: ProductionSchedulePath) => {
        productionSchedulePath.pathGroups.forEach((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup) => {
          if (productionScheduleItemInPathGroup.drawing.drawingType === DrawingType.BMP) {
            const bmpDrawingInPathGroup: BmpProductionScheduleItemInPathGroup = productionScheduleItemInPathGroup as any as BmpProductionScheduleItemInPathGroup;
            if (ObjectUtils.isDeepEqual(bmpDrawingInPathGroup.finishing, oldFinishingForProductionSchedule)) {
              bmpDrawingInPathGroup.changeFinishing(this.productionSchedule.finishing);
            }
          }
        });
      });
    });
  }

  private setFormFields(): void {
    this.productionScheduleInitialPreparationsForm.controls.name.setAsyncValidators(
      AsyncUniqueValidator.createValidator(this.productionSchedules, !this.isCreatingNewProductionSchedule() ? this.productionSchedule.name : null)
    );

    const practicalPickDensityUnit: Unit = Unit[this.getDefaultParameterForPropertyNameOfMachineQualitySettings('pickDensityUnit').propertyValue as string];
    let practicalPickDensityInPicksInUnit = convertUnit({
      from: {
        value: this.productionSchedule.practicalPickDensityInPicksPerMM,
        unit: Unit.PICKS_PER_MILLIMETER
      },
      to: practicalPickDensityUnit
    });

    this.productionScheduleInitialPreparationsForm.controls.practicalPickDensity.setValue(practicalPickDensityInPicksInUnit);
    this.productionScheduleInitialPreparationsForm.controls.productConfiguration.setValue(this.productionSchedule.productConfiguration);
    this.productionScheduleInitialPreparationsForm.controls.buggy.setValue(this.productionSchedule.buggy);

    if (this.areAllFieldsDisabled) {
      this.productionScheduleInitialPreparationsForm.disable();
    }

    setTimeout(() => {
      this.productionScheduleInitialPreparationsForm.controls.name.updateValueAndValidity();
      this.productionScheduleInitialPreparationsForm.controls.name.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy), distinctUntilChanged(), debounceTime(500)).subscribe(() => {
        this.formValuesChanged.emit();
      });
    });

    this.productionScheduleInitialPreparationsFormSubject = this.productionScheduleInitialPreparationsForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      const isDirty: boolean = this.productionScheduleInitialPreparationsForm.dirty;
      if (isDirty) {
        this.notifyChangesToInitialPreparationsForm(isDirty);
      }
    });

    this.productionScheduleInitialPreparationsForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      this.productionScheduleNameGenerationView = {
        creelName: this.productionScheduleInitialPreparationsForm.value.creel?.name,
        machineName: this.productionScheduleInitialPreparationsForm.value.machine?.name,
        machineQualityName: this.productionScheduleInitialPreparationsForm.value.machineQuality?.technicalName,
        buggyName: this.productionScheduleInitialPreparationsForm.value.buggy?.name
      };
    });
  }

  private canSelectMachine(selectedMachine: MachineSummary): boolean {
    return !AssertionUtils.isNullOrUndefined(selectedMachine) && !ObjectUtils.isDeepEqual(this.selectedMachine, selectedMachine);
  }

  public assignMachineToProductionSchedule(selectedMachine: MachineSummary): void {
    if (this.canSelectMachine(selectedMachine)) {
      this.productionScheduleInitialPreparationsForm.controls.practicalPickDensity.setValue(null);

      this.possibleCreelsForCurrentMachine = null;
      this.possibleQualitiesForCurrentMachine = null;

      this.selectedMachine = selectedMachine;
      this.productionSchedule.machine = selectedMachine;
      this.productionScheduleInitialPreparationsForm.controls.machine.setValue(selectedMachine);
      this.productionSchedule.hmiVersion = selectedMachine.hmiVersion;

      forkJoin([
        this.productionOrders.getInitialOptionsForMachine(selectedMachine.id),
        this.creels.getPossibleCreelsForMachine(selectedMachine.id),
        this.machineQualities.getPossibleMachineQualitiesForMachine(this.selectedMachine.id)
      ])
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe(([initialOptionsForProductionOrder, creels, machineQualities]: [InitialOptionsForProductionOrder, OverviewListCreel[], WeaveQuality[]]) => {
          this.possibleCreelsForCurrentMachine = creels;
          this.possibleQualitiesForCurrentMachine = machineQualities;
          this.updateCanHaveRestAndFreeZonesAndPathLabels();

          if (!this.productionSchedule.hasOrderLines()) {
            const activeMachineQuality = machineQualities?.find((machineQuality: WeaveQuality) => {
              return machineQuality.id === initialOptionsForProductionOrder.activeMachineQualityId;
            });
            if (AssertionUtils.isNullOrUndefined(activeMachineQuality)) {
              this.productionScheduleInitialPreparationsForm.get('buggy').disable();
            } else {
              this.productionScheduleInitialPreparationsForm.get('buggy').enable();
            }

            this.getAndAssignMachineQualityToProductionSchedule(activeMachineQuality?.id);
            this.changeDetectorRef.detectChanges();
          }

          this.setEmptyPropertiesFromInitialOptions(this.productionSchedule.machineQuality, initialOptionsForProductionOrder);
        });
    }
  }

  private setEmptyPropertiesFromInitialOptions(machineQuality: MachineQualityForProductionSchedule, initialOptionsForProductionOrder: InitialOptionsForProductionOrder): void {
    if (!AssertionUtils.isNullOrUndefined(machineQuality)) {
      this.getConversionFactorsForMachineQuality(machineQuality).subscribe({
        next: () => {
          this.updateMachineQualityDependentInitialSettingsForProductionSchedule(initialOptionsForProductionOrder);
        },
        error: (error: Error) => {
          this.assignMachineQualityToProductionSchedule(null);
          throw error;
        }
      });
    } else {
      this.getActiveCreelFromListOfPossibleCreelsAndAssignToProductionSchedule(initialOptionsForProductionOrder?.activeCreelId, initialOptionsForProductionOrder?.mappingForCurrentPositionOnMachine);
    }
  }

  private getConversionFactorsForMachineQuality(machineQuality: MachineQualityForProductionSchedule): Observable<any[]> {
    return forkJoin([
      this.machineQualities.getFactorForConversionFromMillimetersToDents(machineQuality.id),
      this.machineQualities.getFactorForConversionFromDentsToMillimeters(machineQuality.id),
      this.machineQualities.getFactorForConversionFromPicksToMillimeters(machineQuality.id),
      this.machineQualities.getFactorForConversionFromMillimetersToPicks(machineQuality.id),
      this.machineQualities.getPathWidths(machineQuality.id)
    ]).pipe(
      tap(
        ([factorForConversionFromMillimetersToDents, factorForConversionFromDentsToMillimeters, factorForConversionFromPicksToMillimeters, factorForConversionFromMillimetersToPicks, pathWidths]: [
          number,
          number,
          number,
          number,
          PathWidth[]
        ]) => {
          this.factorForConversionFromMillimetersToDents = factorForConversionFromMillimetersToDents;
          this.factorForConversionFromDentsToMillimeters = factorForConversionFromDentsToMillimeters;
          this.factorForConversionFromPicksToMillimeters = factorForConversionFromPicksToMillimeters;
          this.factorForConversionFromMillimetersToPicks = factorForConversionFromMillimetersToPicks;
          this.pathWidths = pathWidths;
          this.productionSchedulePlan.applyMeasurementsCalculationsFactor(factorForConversionFromMillimetersToDents);
        }
      ),
      takeUntil(this.unSubscribeOnViewDestroy)
    );
  }

  private adjustObjectsThatDependsOnMachineQuality(machineQuality: MachineQualityForProductionSchedule): void {
    if (!AssertionUtils.isNullOrUndefined(machineQuality)) {
      this.getConversionFactorsForMachineQuality(machineQuality)
        .pipe(
          switchMap(() => this.productionOrders.getInitialOptionsForMachineAndMachineQuality(this.selectedMachine.id, machineQuality.id, machineQuality.reedDensity)),
          takeUntil(this.unSubscribeOnViewDestroy)
        )
        .subscribe({
          next: (initialOptionsForProductionOrder: InitialOptionsForProductionOrder) => {
            this.updateMachineQualityDependentInitialSettingsForProductionSchedule(initialOptionsForProductionOrder);
          },
          error: (error: Error) => {
            this.assignMachineQualityToProductionSchedule(null);
            throw error;
          }
        });
    }
  }

  private assignCreelToProductionSchedule(creel: OverviewListCreel): void {
    if (!AssertionUtils.isNullOrUndefined(creel)) {
      this.productionSchedule.creel = new CreelForProductionSchedule(creel.id, creel.name, creel.isColoredYarnSplitPositionedCorrectly);
      this.productionSchedule.coloredYarnSets = creel.coloredYarnSetsWithStartDent;
      this.productionSchedule.mappingForCurrentPositionOnMachine = creel.mappingForCurrentPositionOnMachine;
      this.productionSchedule.productionSchedulePathsOfColoredYarnSets = [];
      this.productionScheduleInitialPreparationsForm.controls.creel.setValue(creel);
      this.checkIsColoredYarnSetSplitPositionedCorrectly(this.productionSchedule.creel);
    } else {
      this.productionSchedule.creel = null;
      this.productionSchedule.coloredYarnSets = [];
      this.productionSchedule.mappingForCurrentPositionOnMachine = null;
    }

    this.isLoadingMachineQualityAndCreel = false;
    this.finishLoadingProductionScheduleWithSuggestion();
  }

  private initialiseExistingProductionSchedule(): void {
    if (this.isEditingProductionOrder() || this.isDuplicatingProductionOrder()) {
      this.isLoadingProductionSchedule = true;
      this.selectedMachine = this.productionSchedule.machine;

      if (!this.productionSchedule.isProductionScheduleWithEmptyContent()) {
        this.setFormFields();
        this.updateMachineQualityDependentSettingsForExistingProductionSchedule();
        this.checkIsColoredYarnSetSplitPositionedCorrectly(this.productionSchedule.creel);
      } else {
        this.setFormFields();
        this.adjustObjectsThatDependsOnMachineQuality(this.productionSchedule.machineQuality);
      }
    } else {
      this.setFormFields();

      this.selectedMachine = this.productionSchedule.machine;

      if (!AssertionUtils.isNullOrUndefined(this.productionSchedule.machineQuality)) {
        this.updateMachineQualityDependentSettingsForExistingProductionSchedule();
        this.adjustObjectsThatDependsOnMachineQuality(this.productionSchedule.machineQuality);
      } else if (AssertionUtils.isNullOrUndefined(this.productionSchedule.machineQuality) && AssertionUtils.isNullOrUndefined(this.productionSchedule.creel)) {
        this.selectedMachine = null;
        this.assignMachineToProductionSchedule(this.productionSchedule.machine);
      }
    }
  }

  private updateMachineQualityDependentSettingsForExistingProductionSchedule(): void {
    this.getConversionFactorsForMachineQuality(this.productionSchedule.machineQuality)
      .pipe(
        switchMap(() =>
          this.productionOrders.getInitialOptionsForMachineAndMachineQuality(this.selectedMachine.id, this.productionSchedule.machineQuality.id, this.productionSchedule.machineQuality.reedDensity)
        ),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe((initialOptionsForProductionOrder: InitialOptionsForProductionOrder) => {
        this.defaultPathLabelInformation = initialOptionsForProductionOrder.initialPathLabel;
        this.defaultInitialRestZones = initialOptionsForProductionOrder.initialRestZones;

        this.finishLoadingProductionScheduleWithSuggestion();

        if (!this.canShowJuteOptions()) {
          this.juteZoneLoaded.emit();
        }

        if (!this.canInsertPathLabels()) {
          this.pathZoneLoaded.emit();
        }

        if (!AssertionUtils.isNullOrUndefined(this.defaultInitialRestZones.pattern)) {
          this.restZoneLoaded.emit();
        }
      });
  }

  private updateMachineQualityDependentInitialSettingsForProductionSchedule(initialOptionsForProductionOrder: InitialOptionsForProductionOrder): void {
    if (AssertionUtils.isNullOrUndefined(this.productionSchedule.finishing)) {
      this.productionSchedule.finishing = initialOptionsForProductionOrder.finishing;
    }

    if (
      AssertionUtils.isNullOrUndefined(this.productionSchedule.pathLabelInformation) &&
      !AssertionUtils.isNullOrUndefined(initialOptionsForProductionOrder.initialPathLabel) &&
      (initialOptionsForProductionOrder.initialPathLabel.pathLabelInformationBefore?.nameGenerator?.parts.length > 0 ||
        initialOptionsForProductionOrder.initialPathLabel.pathLabelInformationAfter?.nameGenerator?.parts.length > 0)
    ) {
      this.productionSchedule.pathLabelInformation = new PathLabelInformation(
        initialOptionsForProductionOrder.initialPathLabel.pathLabelInformationAfter,
        initialOptionsForProductionOrder.initialPathLabel.pathLabelInformationBefore,
        initialOptionsForProductionOrder.initialPathLabel.fontSettings,
        initialOptionsForProductionOrder.initialPathLabel.labelHeightInMM,
        initialOptionsForProductionOrder.initialPathLabel.labelHeightInPicks,
        initialOptionsForProductionOrder.initialPathLabel.pattern
      );

      this.defaultPathLabelInformation = initialOptionsForProductionOrder.initialPathLabel;
    }

    this.updateJuteAndZones(initialOptionsForProductionOrder);

    const practicalPickDensityUnit: Unit = Unit[this.getDefaultParameterForPropertyNameOfMachineQualitySettings('pickDensityUnit').propertyValue as string];
    const practicalPickDensityInPicksPerMM = AssertionUtils.isNumber(initialOptionsForProductionOrder.practicalPickDensityInPicksPerMM)
      ? initialOptionsForProductionOrder.practicalPickDensityInPicksPerMM
      : this.productionSchedule.machineQuality.practicalPickDensity;
    const practicalPickDensityInCustomUnit: number = convertUnit({
      from: {
        value: practicalPickDensityInPicksPerMM,
        unit: Unit.PICKS_PER_MILLIMETER
      },
      to: practicalPickDensityUnit
    });

    if (!this.hasMachineExceptionForPracticalPickDensity) {
      this.productionScheduleInitialPreparationsForm.controls.practicalPickDensity.setValue(practicalPickDensityInCustomUnit);
    }

    if ((!this.isAddingProductionOrderForRun() && initialOptionsForProductionOrder.activeCreelId) || this.isAddingProductionOrderForProductConfiguration()) {
      this.productionSchedule.mappingForCurrentPositionOnMachine = initialOptionsForProductionOrder.mappingForCurrentPositionOnMachine;

      if (this.possibleCreelsForCurrentMachine) {
        this.getActiveCreelFromListOfPossibleCreelsAndAssignToProductionSchedule(initialOptionsForProductionOrder.activeCreelId, initialOptionsForProductionOrder.mappingForCurrentPositionOnMachine);
      }
    }

    this.finishLoadingProductionScheduleWithSuggestion();
    this.changeDetectorRef.detectChanges();

    if (!this.canInsertPathLabels()) {
      this.pathZoneLoaded.emit();

      const juteInformation = this.productionSchedule.juteInformation;

      if (AssertionUtils.isNullOrUndefined(juteInformation?.juteBefore) && AssertionUtils.isNullOrUndefined(juteInformation?.juteAfter)) {
        this.juteZoneLoaded.emit();
      }
    }

    if (!this.canShowJuteOptions()) {
      this.juteZoneLoaded.emit();
    }
  }

  private updateJuteAndZones(initialOptionsForProductionOrder: InitialOptionsForProductionOrder): void {
    if (AssertionUtils.isNullOrUndefined(this.productionSchedule.juteInformation)) {
      this.productionSchedule.juteInformation = initialOptionsForProductionOrder.initialJute;
    }
    if (AssertionUtils.isNullOrUndefined(this.productionSchedule.initialFreeZones)) {
      this.productionSchedule.initialFreeZones = initialOptionsForProductionOrder.initialFreeZones;
    }
    if (AssertionUtils.isNullOrUndefined(this.productionSchedule.initialRestZones)) {
      this.productionSchedule.initialRestZones = initialOptionsForProductionOrder.initialRestZones;
    }
    if (AssertionUtils.isNullOrUndefined(this.defaultInitialRestZones)) {
      this.defaultInitialRestZones = cloneDeep(initialOptionsForProductionOrder.initialRestZones);
    }
  }

  private initialiseNewProductionSchedule(): void {
    this.setFormFields();

    let request: Observable<InitialProductionOrderForTypeRequestProperties>;

    if (this.isAddingProductionOrderForMachine()) {
      request = this.getRequestForProductionOrderForMachine();
    } else if (this.isAddingProductionOrderForRun()) {
      request = this.getRequestForProductionOrderForRun();
    } else if (this.isAddingProductionOrderForProductConfiguration()) {
      request = this.getRequestForProductionOrderForProductConfiguration();
    }

    if (this.isAddingProductionOrderForType()) {
      this.isLoadingMachineQualityAndCreel = true;

      request?.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((results: [MachineSummary, WeaveQuality, OverviewListCreel, ProductConfiguration]) => {
        this.initialiseProductionScheduleForType(...results);
      });
    }
  }

  public initialiseProductionScheduleForType(machine: MachineSummary, machineQuality: WeaveQuality, creel: OverviewListCreel, productConfiguration: ProductConfiguration): void {
    this.selectedMachine = machine;
    this.productionSchedule.machine = machine;
    this.productionScheduleInitialPreparationsForm.controls.machine.setValue(machine);
    this.productionSchedule.hmiVersion = machine.hmiVersion;

    const hasMachineQuality = !AssertionUtils.isNullOrUndefined(machineQuality);
    const hasCreel = !AssertionUtils.isNullOrUndefined(creel);

    if (hasMachineQuality) {
      this.assignMachineQualityToProductionSchedule(machineQuality);
    }

    if (hasCreel) {
      this.assignCreelToProductionSchedule(creel);
    }

    if (!AssertionUtils.isNullOrUndefined(productConfiguration)) {
      const version = productConfiguration.versionHistories.find((versionHistory: VersionHistory) => versionHistory.readyForProduction)?.version ?? productConfiguration.versionHistories[0].version;
      this.productionScheduleInitialPreparationsForm.controls.productConfiguration.setValue(new ProductionScheduleProductConfiguration(productConfiguration.id, productConfiguration.name, version));

      if (productConfiguration.designs) {
        this.productionSchedule.designs = productConfiguration.designs;
      }
    }

    forkJoin([
      this.isAddingProductionOrderForProductConfiguration() || this.isAddingProductionOrderForRun()
        ? this.productionOrders.getInitialOptionsForMachineAndMachineQuality(machine.id, machineQuality.id, machineQuality.reedDensityInDentsPerMM)
        : this.productionOrders.getInitialOptionsForMachine(machine.id),
      this.creels.getPossibleCreelsForMachine(machine.id),
      this.machineQualities.getPossibleMachineQualitiesForMachine(machine.id)
    ])
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(([initialOptionsForProductionOrder, creels, machineQualities]: [InitialOptionsForProductionOrder, OverviewListCreel[], WeaveQuality[]]) =>
        this.setAdditionalOptionsForProductionOrderForType(hasMachineQuality, hasCreel, initialOptionsForProductionOrder, creels, machineQualities)
      );

    this.updateCanHaveRestAndFreeZonesAndPathLabels();
  }

  private setAdditionalOptionsForProductionOrderForType(
    hasMachineQuality: boolean,
    hasCreel: boolean,
    initialOptionsForProductionOrder: InitialOptionsForProductionOrder,
    creels: OverviewListCreel[],
    machineQualities: WeaveQuality[]
  ): void {
    this.possibleCreelsForCurrentMachine = creels;
    this.possibleQualitiesForCurrentMachine = machineQualities;

    const possibleMachineQualityFromInitialOptions = this.possibleQualitiesForCurrentMachine.find(
      (possibleMachineQuality: WeaveQuality) => possibleMachineQuality.id === initialOptionsForProductionOrder.activeMachineQualityId
    );
    if (!hasMachineQuality && !AssertionUtils.isNullOrUndefined(possibleMachineQualityFromInitialOptions)) {
      this.assignMachineQualityToProductionSchedule(possibleMachineQualityFromInitialOptions);
    }

    const possibleCreelFromInitialOptions = this.possibleCreelsForCurrentMachine.find((possibleCreel: OverviewListCreel) => possibleCreel.id === initialOptionsForProductionOrder.activeCreelId);
    if (!hasCreel && !AssertionUtils.isNullOrUndefined(possibleCreelFromInitialOptions)) {
      this.assignCreelToProductionSchedule(possibleCreelFromInitialOptions);
    } else if (hasCreel) {
      this.productionSchedule.coloredYarnSets =
        this.possibleCreelsForCurrentMachine.find((possibleCreel: OverviewListCreel) => possibleCreel.id === this.productionSchedule.creel.id)?.coloredYarnSetsWithStartDent ??
        this.possibleCreelsForCurrentMachine[0].coloredYarnSetsWithStartDent;

      this.productionSchedule.creel.isColoredYarnSplitPositionedCorrectly = this.possibleCreelsForCurrentMachine.find(
        (possibleCreel: OverviewListCreel) => possibleCreel.id === this.productionSchedule.creel.id
      )?.isColoredYarnSplitPositionedCorrectly;

      this.checkIsColoredYarnSetSplitPositionedCorrectly(this.productionSchedule.creel);
    }

    this.setEmptyPropertiesFromInitialOptions(this.productionSchedule.machineQuality, initialOptionsForProductionOrder);

    this.isLoadingMachineQualityAndCreel = false;
  }

  private getRequestForProductionOrderForMachine(): Observable<InitialProductionOrderForTypeRequestProperties> {
    const machineId = this.activatedRoute.snapshot.params.id as number;

    return forkJoin([this.machines.getSummaryForMachine(machineId), of(null), of(null), of(null)]);
  }

  private getRequestForProductionOrderForRun(): Observable<InitialProductionOrderForTypeRequestProperties> {
    const {runId, machineQualityId, creelId, machineId} = this.navigationHistory.getState() as {
      runId: number;
      machineQualityId: number;
      creelId: number;
      machineId: number;
    };

    if (AssertionUtils.isNullOrUndefined(runId) || AssertionUtils.isNullOrUndefined(machineQualityId) || AssertionUtils.isNullOrUndefined(machineId)) {
      this.router.navigateByUrl(NavigationUtils.getAbsolutePath(NavigationId.PLANNING));
      return of(null);
    }

    let getCreelMapForRun: Observable<CreelMap[]>;
    if (this.authentication.getCurrentSubscription()?.hasPermission(Permission.TEXFAB_PLANNINGBOARD_NEW)) {
      getCreelMapForRun = this.planningPrototype.getCreelMapForRun(runId);
    } else {
      getCreelMapForRun = this.planning.getCreelMapForRun(runId);
    }

    return forkJoin([this.machines.getSummaryForMachine(machineId), this.machineQualities.getById(machineQualityId), this.creels.getById(creelId), getCreelMapForRun]).pipe(
      map(([machine, machineQuality, creel, mappingForCurrentPositionOnMachine]: [MachineSummary, WeaveQuality, OverviewListCreel, CreelMap[]]) => {
        creel.mappingForCurrentPositionOnMachine = mappingForCurrentPositionOnMachine;
        return [machine, machineQuality, creel, null];
      }),
      tap(() => {
        this.productionSchedule.runId = runId;
      })
    ) as Observable<InitialProductionOrderForTypeRequestProperties>;
  }

  private getRequestForProductionOrderForProductConfiguration(): Observable<InitialProductionOrderForTypeRequestProperties> {
    const productConfigurationId: number = this.activatedRoute.snapshot.params.id;

    const productConfigurationObservable = this.productConfigurations.getById(productConfigurationId);

    return forkJoin([
      productConfigurationObservable.pipe(switchMap((productConfiguration: ProductConfiguration) => this.machines.getSummaryForMachine(productConfiguration.machine.id))),
      productConfigurationObservable.pipe(switchMap((productConfiguration: ProductConfiguration) => this.machineQualities.getById(productConfiguration.quality.id))),
      of(null),
      productConfigurationObservable
    ])?.pipe(
      tap(() => {
        this.productionSchedule.isSample = true;
      })
    );
  }

  private finishLoadingProductionScheduleWithSuggestion(): void {
    this.finishLoadingProductionSchedule();
    this.refreshNameGeneration.next();
  }

  private finishLoadingProductionSchedule(): void {
    this.isLoadingProductionSchedule = false;

    const getFunctionsBasedOnDialogComponent = new Map<any, NavigationFunctionData>([
      [SelectMachineQualityComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setNewQuality(newItemId), reopenDialog: (): void => this.selectAnotherMachineQuality()}],
      [SelectCreelComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setNewCreel(newItemId), reopenDialog: (): void => this.selectAnotherCreel()}],
      [SelectBuggyComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setNewBuggy(newItemId), reopenDialog: (): void => this.selectBuggy()}]
    ]);

    this.navigationHelperService.setNewItemOrReopenDialogIfPresent(
      ({newItemId}: NavigationNewItemData, {dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).loadNewItem({newItemId}),
      ({dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).reopenDialog()
    );

    this.showAdditionalOptions();
  }

  private setNewQuality(id: number): void {
    this.machineQualities
      .getPossibleMachineQualitiesForMachine(this.selectedMachine.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((possibleQualitiesForCurrentMachine: WeaveQuality[]) => {
        this.possibleQualitiesForCurrentMachine = possibleQualitiesForCurrentMachine;
        this.setQuality(OverviewListMachineQuality.fromCarpetWeaveQuality(possibleQualitiesForCurrentMachine.find((quality: WeaveQuality) => quality.id === id) as CarpetWeaveQuality));
      });
  }

  private setNewCreel(id: number): void {
    this.creels
      .getPossibleCreelsForMachine(this.selectedMachine.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((possibleCreelsForCurrentMachine: OverviewListCreel[]) => {
        this.possibleCreelsForCurrentMachine = possibleCreelsForCurrentMachine;
        this.setCreel(possibleCreelsForCurrentMachine.find((creel: OverviewListCreel) => creel.id === id));
      });
  }

  private setNewBuggy(id: number): void {
    this.buggies
      .getById(id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((buggy: BuggyForProductionSchedule) => {
        this.productionSchedule.buggy = buggy;
        this.productionScheduleInitialPreparationsForm.controls.buggy.setValue(buggy);
      });
  }

  private showAdditionalOptions(): void {
    const {machineQuality, coloredYarnSets} = this.productionSchedule;
    this.canShowAdditionalOptions = !AssertionUtils.isNullOrUndefined(machineQuality) && coloredYarnSets.length > 0;

    if (this.canShowAdditionalOptions && this.dialogState?.dialogComponent === SelectFinishingComponent) {
      setTimeout(() => this.initialPreparationsAdditionalOptionsComponent.selectFinishing());
    }
  }

  private onMachineSelect(selectedMachine: Equipment): void {
    const hasSelectedNewMachine = !AssertionUtils.isNullOrUndefined(selectedMachine) && !this.hasChosenMachine();
    const hasSelectedAnotherMachine = !AssertionUtils.isNullOrUndefined(selectedMachine) && this.hasChosenMachine() && this.selectedMachine.id !== selectedMachine.id;

    if (!hasSelectedAnotherMachine) {
      this.productionScheduleInitialPreparationsForm.controls.machine.setValue(selectedMachine);
    }

    if (hasSelectedNewMachine || hasSelectedAnotherMachine) {
      this.canShowAdditionalOptions = false;
      this.possibleQualitiesForCurrentMachine = null;
      this.isLoadingMachineQualityAndCreel = true;

      this.machines
        .getSummaryForMachine(selectedMachine.id)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((machineSummaryOfSelectedMachine: MachineSummary) => {
          this.assignMachineToProductionSchedule(machineSummaryOfSelectedMachine);
        });

      this.formValuesChanged.emit();

      if (hasSelectedAnotherMachine && this.hasChosenMachine()) {
        this.notifyChangesToInitialPreparationsForm(true);
      }
    }
  }

  private notifyChangesToInitialPreparationsForm(value: boolean): void {
    if (!this.productionScheduleInitialPreparationsFormSubject?.closed) {
      this.addProductionSchedule.notifyChangesToInitialPreparationsForm(value);
      this.productionScheduleInitialPreparationsFormSubject?.unsubscribe();
    }
  }

  private getAndAssignMachineQualityToProductionSchedule(id: number, canAddJute: boolean = false, adjustObjectsThatDependsOnMachineQuality: boolean = true): void {
    this.machineQualities
      .getById(id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((weaveQuality: WeaveQuality) => {
        this.hasMachineExceptionForPracticalPickDensity = false;

        this.assignMachineQualityToProductionSchedule(weaveQuality);

        this.productionSchedule.canAddJute = canAddJute ?? weaveQuality.canAddJute;

        if (adjustObjectsThatDependsOnMachineQuality) {
          this.adjustObjectsThatDependsOnMachineQuality(this.productionSchedule.machineQuality);

          this.formValuesChanged.emit();
          this.notifyChangesToInitialPreparationsForm(true);
        }
      });
  }

  private updateBuggyFormField(): void {
    this.productionScheduleInitialPreparationsForm.get('buggy').setValue(this.buggy);
  }

  private checkIsColoredYarnSetSplitPositionedCorrectly(currentCreel: CreelForProductionSchedule): void {
    this.canShowWarningForIncorrectSplittedCreel = !currentCreel.isColoredYarnSplitPositionedCorrectly;
  }

  private assignMachineQualityToProductionSchedule(activeWeaveQuality: WeaveQuality): void {
    this.productionSchedule.machineQuality = !AssertionUtils.isNullOrUndefined(activeWeaveQuality) ? MachineQualityForProductionSchedule.fromWeaveQuality(activeWeaveQuality) : null;
    this.productionScheduleInitialPreparationsForm.controls.machineQuality.setValue(activeWeaveQuality);
    this.productionSchedule.canAddJute = activeWeaveQuality?.canAddJute ?? false;

    activeWeaveQuality?.machinesWithSpecificPickDensity?.forEach((machineWithSpecificPickDensity: {machine: MachineOverviewList; specificPickDensity: number}) => {
      if (machineWithSpecificPickDensity?.machine?.id === this.selectedMachine?.id) {
        this.hasMachineExceptionForPracticalPickDensity = true;
        this.productionScheduleInitialPreparationsForm.controls.practicalPickDensity.setValue(machineWithSpecificPickDensity?.specificPickDensity * 1000);
        this.productionSchedule.practicalPickDensityInPicksPerMM = machineWithSpecificPickDensity?.specificPickDensity * 1000;
      }
    });

    if (!this.canShowJuteOptions()) {
      this.juteZoneLoaded.emit();
    }
  }

  private getActiveCreelFromListOfPossibleCreelsAndAssignToProductionSchedule(activeCreelId: number, mappingForCurrentPositionOnMachine: CreelMap[]): void {
    const creel = this.possibleCreelsForCurrentMachine.find((overviewListCreel: OverviewListCreel) => overviewListCreel.id === activeCreelId);

    if (creel) {
      creel.mappingForCurrentPositionOnMachine = mappingForCurrentPositionOnMachine;
    }

    this.assignCreelToProductionSchedule(creel);
  }

  private updateCanHaveRestAndFreeZonesAndPathLabels(): void {
    this.canHaveRestAndFreeZonesAndPathLabels = this.productionSchedule?.machine?.canHaveRestAndFreeZonesAndPathLabels();
  }

  private checkIfBuggyHasNameGeneration(): void {
    if (this.currentSubscription?.hasPermission(Permission.VIEW_BUGGIES) ?? false) {
      this.httpBuggyCustomSettings
        .get()
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((customSettings: CustomSettings) => {
          this.hasBuggyNameGeneration = customSettings?.nameGenerator?.parts?.length > 0;
        });
    }
  }
}
